import { useContext, useEffect } from 'react';
import { SEOContext } from '@/lib/SEOContext';
import { useLocation } from 'react-router-dom';

const SITE = 'https://oliverandsons.com.au';
const BUSINESS_ID = `${SITE}/#organization`;

function titleCase(value) {
  return decodeURIComponent(value)
    .split('-')
    .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' ');
}

function getContext(pathname) {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/') {
    return { breadcrumb: null, service: null };
  }

  const plumber = path.match(/^\/commercial-plumber-(.+)$/);
  if (plumber) {
    const location = titleCase(plumber[1]);
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Commercial Plumbing', item: SITE + '/services' },
        { name: `Commercial Plumber ${location}`, item: SITE + path },
      ],
      service: {
        name: `Commercial Plumbing ${location}`,
        serviceType: 'Commercial Plumbing',
        description: `Commercial plumbing services in ${location} for builders, shopfitters and project teams, including hydraulic rough-ins, fit-outs, trade waste and related plumbing works.`,
        area: location,
      },
    };
  }

  const bathroom = path.match(/^\/commercial-bathroom-plumbing-(.+)$/);
  if (bathroom) {
    const location = titleCase(bathroom[1]);
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Commercial Plumbing Services', item: SITE + '/services' },
        { name: `Commercial Bathroom Plumbing ${location}`, item: SITE + path },
      ],
      service: {
        name: `Commercial Bathroom Plumbing ${location}`,
        serviceType: 'Commercial Bathroom Plumbing',
        description: `Commercial bathroom plumbing in ${location} for fit-outs, amenities, refurbishments, hydraulic rough-ins and final fixture connections.`,
        area: location,
      },
    };
  }

  const grease = path.match(/^\/grease-pit-plumbing-(.+)$/);
  if (grease) {
    const location = titleCase(grease[1]);
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Commercial Plumbing Services', item: SITE + '/services' },
        { name: `Grease Pit & Grease Trap Plumbing ${location}`, item: SITE + path },
      ],
      service: {
        name: `Grease Pit & Grease Trap Plumbing ${location}`,
        serviceType: 'Grease Pit and Grease Trap Plumbing',
        description: `Commercial grease pit and grease trap plumbing in ${location}, including trade waste pipework, arrestor connections, replacements and commercial kitchen plumbing.`,
        area: location,
      },
    };
  }

  if (/^\/commercial-car-wash-plumbing-/.test(path)) {
    return { breadcrumb: null, service: null };
  }

  if (path === '/services') {
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Commercial Plumbing Services', item: SITE + path },
      ],
      service: {
        name: 'Commercial Plumbing Services Brisbane & South East Queensland',
        serviceType: 'Commercial Plumbing',
        description: 'Commercial plumbing services across Brisbane and South East Queensland, including hydraulic rough-ins, shop fit-outs, commercial kitchens, bathrooms, trade waste, backflow and project coordination.',
        area: 'Brisbane and South East Queensland',
      },
    };
  }

  if (path === '/industries') {
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Industries', item: SITE + path },
      ],
      service: null,
    };
  }

  if (path === '/projects') {
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Projects', item: SITE + path },
      ],
      service: null,
    };
  }

  if (path === '/service-areas') {
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Service Areas', item: SITE + path },
      ],
      service: null,
    };
  }

  if (path === '/request-a-quote') {
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Request a Quote', item: SITE + path },
      ],
      service: null,
    };
  }

  if (path === '/privacy-policy' || path === '/terms-and-conditions') {
    return {
      breadcrumb: [
        { name: 'Home', item: SITE + '/' },
        { name: 'Legal', item: SITE + '/privacy-policy' },
        { name: path === '/privacy-policy' ? 'Privacy Policy' : 'Terms & Conditions', item: SITE + path },
      ],
      service: null,
    };
  }

  return { breadcrumb: null, service: null };
}

export function buildStructuredData(pathname) {
    const context = getContext(pathname);

    const organization = {
      '@type': 'Organization',
      '@id': BUSINESS_ID,
      name: 'Oliver & Sons Plumbing',
      url: SITE + '/',
      logo: `${SITE}/images/optimized/performance/logo-280.webp`,
      telephone: '+61 403 448 370',
      description: 'Commercial plumbing business serving Brisbane and South East Queensland, with a focus on plumbing for builders, shopfitters and commercial projects.',
    };

    const graph = [
      { '@type': 'WebSite', '@id': SITE + '/#website', url: SITE + '/', name: 'Oliver & Sons Plumbing', publisher: { '@id': BUSINESS_ID } },
      organization,
    ];

    if (context.service) {
      graph.push({
        '@type': 'Service',
        '@id': SITE + pathname + '#service',
        name: context.service.name,
        serviceType: context.service.serviceType,
        description: context.service.description,
        url: SITE + pathname,
        provider: { '@id': BUSINESS_ID },
        areaServed: { '@type': 'AdministrativeArea', name: context.service.area },
      });
    }

    if (context.breadcrumb) {
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': SITE + pathname + '#breadcrumb',
        itemListElement: context.breadcrumb.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.item,
        })),
      });
    }

    return { '@context': 'https://schema.org', '@graph': graph };
}

export default function SiteStructuredData() {
  const { pathname } = useLocation();
  const collector = useContext(SEOContext);
  if (collector) collector.structuredData = buildStructuredData(pathname);
  useEffect(() => {
    const id = 'site-structured-data';
    document.getElementById(id)?.remove();
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(buildStructuredData(pathname));
    document.head.appendChild(script);

    return () => document.getElementById(id)?.remove();
  }, [pathname]);

  return null;
}

